/**
 * Knowledge — Phase 2 permission-filtered document browser.
 *
 * Lists ONLY the documents the current persona is entitled to see (GET
 * /api/retrieval/documents runs the full corpus through the same deterministic
 * permission filter used by retrieval), as a filterable, paginated table.
 *
 * Suppressed documents are surfaced as a WITHHELD COUNT only — their titles,
 * content, urls, and metadata are never rendered — so an unentitled persona
 * cannot infer restricted content from the browser.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { SensitivityBadgeList } from '../components/SensitivityBadge';
import type { DocumentsResponse, PermittedDoc } from '../lib/retrieval';

const PAGE_SIZE = 8;

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return value;
  return new Date(ms).toLocaleDateString();
}

/** Leak-free banner summarizing how many docs the persona may/may-not see. */
function CorpusSummary({ data }: { data: DocumentsResponse }): ReactNode {
  const withheld = data.withheld_count;
  return (
    <section
      data-testid="knowledge-summary"
      className={`rounded-lg border p-4 ${
        withheld > 0
          ? 'border-amber-300 bg-amber-50'
          : 'border-emerald-200 bg-emerald-50'
      }`}
    >
      <p className="text-sm font-medium text-navy-900">
        You can access{' '}
        <span className="font-semibold">{data.permitted_count}</span> of{' '}
        <span className="font-semibold">{data.total_in_corpus}</span> documents in
        the corpus.
      </p>
      {withheld > 0 ? (
        <p
          data-testid="knowledge-withheld-notice"
          className="mt-1 text-sm font-semibold text-amber-900"
        >
          {data.suppression_notice ??
            `${withheld} ${
              withheld === 1 ? 'document' : 'documents'
            } withheld due to your entitlements`}
        </p>
      ) : (
        <p className="mt-1 text-xs text-emerald-800">
          You are entitled to view every document in the corpus.
        </p>
      )}
    </section>
  );
}

function DocumentsTable({ docs }: { docs: PermittedDoc[] }): ReactNode {
  return (
    <div className="overflow-x-auto rounded-lg border border-steel-200">
      <table className="w-full text-sm">
        <thead className="bg-steel-50 text-left text-[12px] uppercase tracking-wide text-steel-500">
          <tr>
            <th className="px-4 py-2 font-medium">Document</th>
            <th className="px-4 py-2 font-medium">Sensitivity</th>
            <th className="px-4 py-2 font-medium">Source</th>
            <th className="px-4 py-2 font-medium">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-steel-100">
          {docs.map((doc) => (
            <tr
              key={doc.chunk_id ?? doc.doc_id ?? doc.title ?? Math.random()}
              data-testid="knowledge-row"
              className="hover:bg-steel-50"
            >
              <td className="px-4 py-2">
                <div className="font-medium text-navy-900">
                  {doc.title ?? 'Untitled document'}
                </div>
                {doc.site_or_drive && (
                  <div className="text-xs text-steel-500">{doc.site_or_drive}</div>
                )}
              </td>
              <td className="px-4 py-2">
                <SensitivityBadgeList sensitivity={doc.sensitivity} />
              </td>
              <td className="px-4 py-2 text-steel-600">{doc.source_system ?? '—'}</td>
              <td className="px-4 py-2 text-steel-600">
                {formatDate(doc.last_modified) ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Knowledge(): ReactNode {
  const { user } = useAuth();
  const persona = user?.personaKey ?? user?.email;

  const { data, isLoading, isError, error, refetch } = useQuery<DocumentsResponse>({
    queryKey: ['retrieval-documents', persona],
    queryFn: () => api.get<DocumentsResponse>('/retrieval/documents'),
  });

  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);

  // Reset to the first page when the persona or filter changes.
  useEffect(() => setPage(0), [persona, filter]);

  const allDocs = data?.documents ?? [];
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return allDocs;
    return allDocs.filter((d) => (d.title ?? '').toLowerCase().includes(q));
  }, [allDocs, filter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * PAGE_SIZE;
  const pageDocs = filtered.slice(start, start + PAGE_SIZE);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-5 animate-fade-in">
      <header>
        <h2 className="text-xl font-semibold text-navy-900">Knowledge Browser</h2>
        <p className="mt-1 text-sm text-steel-600">
          Documents the{' '}
          <span className="font-medium text-navy-700">{user?.role ?? 'current'}</span>{' '}
          role may access, filtered by Entra entitlements. Restricted documents are
          counted but never shown.
        </p>
      </header>

      {isLoading && (
        <div className="card text-sm text-steel-500">Loading documents…</div>
      )}

      {isError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          <p>{error instanceof Error ? error.message : 'Failed to load documents.'}</p>
          <button type="button" onClick={() => refetch()} className="mt-2 btn-primary">
            Retry
          </button>
        </div>
      )}

      {data && (
        <>
          <CorpusSummary data={data} />

          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter documents by title…"
            className="rounded-lg border border-steel-200 px-3 py-2 text-sm text-navy-900 focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-500"
          />

          {filtered.length > 0 ? (
            <>
              <DocumentsTable docs={pageDocs} />
              <div className="flex items-center justify-between text-xs text-steel-500">
                <span>
                  Showing {start + 1}–{Math.min(start + PAGE_SIZE, filtered.length)} of{' '}
                  {filtered.length}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="knowledge-prev"
                    disabled={safePage === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className="rounded-lg border border-steel-200 px-3 py-1 disabled:opacity-40 hover:bg-steel-50"
                  >
                    Prev
                  </button>
                  <span>
                    Page {safePage + 1} of {pageCount}
                  </span>
                  <button
                    type="button"
                    data-testid="knowledge-next"
                    disabled={safePage >= pageCount - 1}
                    onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                    className="rounded-lg border border-steel-200 px-3 py-1 disabled:opacity-40 hover:bg-steel-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="card text-sm text-steel-500">
              {allDocs.length === 0
                ? 'No documents are available to your current entitlements.'
                : 'No documents match your filter.'}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Knowledge;
